import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { theme as baseTheme } from '../theme';
import { useFacility } from '../context/FacilityContext';
import { FACILITY_IDS, SP_THEME } from '../utils/facilities';

const NAV_HEIGHT = 56;
const MENU_PANEL_WIDTH = 320;

const NavContainer = styled.nav`
  box-sizing: border-box;
  background-color: var(--al-bg);
  border-bottom: 1px solid var(--al-border);
  padding: 0 ${baseTheme.spacing.lg};
  height: ${NAV_HEIGHT}px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${baseTheme.spacing.xs};
  font-family: ${baseTheme.fonts.primary};
  transition: background-color 0.25s ease, border-color 0.25s ease;

  @media (max-width: ${baseTheme.breakpoints.lg}) {
    padding: 0 ${baseTheme.spacing.sm};
  }
`;

const FacilitySwitcher = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid ${(p) => (p.$sp ? 'rgba(223, 174, 76, 0.4)' : 'rgba(var(--al-primary-rgb), 0.35)')};
  height: 36px;
`;

const FacilityOption = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  flex: 1;
  padding: 0 10px;
  margin: 0;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1;
  transition: background-color 0.2s ease, color 0.2s ease;
  font-family: ${(p) => (p.$spActive ? SP_THEME.fonts.caps : baseTheme.fonts.primary)};

  ${(p) =>
    p.$active
      ? p.$spActive
        ? `
    /* Vine panel + Harvest caps (Farmstand dark card) */
    background: ${SP_THEME.vine};
    color: ${SP_THEME.harvest};
  `
        : `
    background: var(--al-primary);
    color: var(--al-bg);
  `
      : p.$spChrome
        ? `
    background: transparent;
    color: ${SP_THEME.flour};
    &:hover { background: rgba(223, 174, 76, 0.1); color: ${SP_THEME.harvest}; }
  `
        : `
    background: transparent;
    color: rgba(255,255,255,0.65);
    &:hover { background: rgba(var(--al-primary-rgb), 0.12); color: var(--al-primary); }
  `}
`;

const FacilityCapsWord = styled.span`
  font-family: ${SP_THEME.fonts.caps};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const FacilityScriptWord = styled.span`
  font-family: ${SP_THEME.fonts.script};
  font-size: 1.05em;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 0.15em;
  line-height: 1;
  /* Cherry on cocoa; flour script on vine (active SP chip) */
  color: ${(p) => (p.$onVine ? SP_THEME.flour : SP_THEME.cherry)};
`;

const navTypography = `
  font-size: ${baseTheme.typography.nav.fontSize};
  font-weight: ${baseTheme.typography.nav.fontWeight};
  text-transform: ${baseTheme.typography.nav.textTransform};
  letter-spacing: ${baseTheme.typography.nav.letterSpacing};
`;

const FacilityMenuSection = styled.div`
  margin-top: auto;
  padding: ${baseTheme.spacing.md} ${baseTheme.spacing.lg};
  border-top: 1px solid var(--al-border);
`;

const FacilityMenuLabel = styled.div`
  ${navTypography}
  font-size: 0.65rem;
  color: var(--al-text-dim);
  letter-spacing: 0.12em;
  margin-bottom: ${baseTheme.spacing.sm};
`;

const LogoLink = styled(Link)`
  font-size: 1.25rem;
  font-weight: ${baseTheme.typography.heading.fontWeight};
  text-transform: ${baseTheme.typography.heading.textTransform};
  letter-spacing: 0.12em;
  color: var(--al-text);
  text-decoration: none;
  flex-shrink: 0;

  &:hover {
    color: var(--al-primary);
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${baseTheme.spacing.sm};
  flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${props => (props.$menuOpen ? -MENU_PANEL_WIDTH : 0)}px);
`;

const MainNav = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: ${baseTheme.spacing.sm};
  align-items: center;
  @media (max-width: ${baseTheme.breakpoints.lg}) {
    display: none;
  }
`;

/** Same destinations as MainNav, visible only under lg — bar was empty except hamburger on phones/tablets. */
const CompactNav = styled.ul`
  display: none;
  list-style: none;
  margin: 0;
  padding: 0 ${baseTheme.spacing.sm};
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${baseTheme.breakpoints.lg}) {
    display: flex;
  }

  li a {
    display: block;
    white-space: nowrap;
    font-size: 0.65rem;
    font-weight: ${baseTheme.fontWeights.semiBold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--al-text);
    text-decoration: none;
    padding: 6px 8px;
    border-radius: 4px;
    transition: color 0.2s ease, background-color 0.2s ease;
  }

  li a:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.08);
  }
`;

const CompactNavItem = styled.li`
  flex-shrink: 0;

  a {
    color: ${(p) => (p.$active ? 'var(--al-primary)' : 'var(--al-text)')} !important;
    background-color: ${props => (props.$active ? 'rgba(var(--al-primary-rgb), 0.12)' : 'transparent')};
  }
`;

const NavItem = styled.li`
  a {
    ${navTypography}
    font-size: 0.75rem;
    color: ${(p) => (p.isActive ? 'var(--al-primary)' : 'var(--al-text)')};
    text-decoration: none;
    padding: ${baseTheme.spacing.sm} ${baseTheme.spacing.md};
    border-radius: 4px;
    background-color: ${props => (props.isActive ? 'rgba(var(--al-primary-rgb), 0.12)' : 'transparent')};
    transition: color 0.2s ease, background-color 0.2s ease;

    &:hover {
      color: var(--al-primary);
      background-color: rgba(var(--al-primary-rgb), 0.08);
    }
  }
`;

const HamburgerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HamburgerButton = styled.button`
  width: 44px;
  height: 44px;
  margin: 0 -8px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--al-text);
  border-radius: 8px;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.08);
  }

  &:focus {
    outline: none;
  }
`;

const HamburgerIcon = styled.span`
  width: 22px;
  height: 16px;
  position: relative;
  display: block;

  span {
    display: block;
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: currentColor;
    border-radius: 1px;
    transition: transform 0.25s ease, opacity 0.2s ease;
  }

  span:nth-of-type(1) {
    top: 0;
    transform: ${props => (props.isOpen ? 'translateY(7px) rotate(45deg)' : 'none')};
  }
  span:nth-of-type(2) {
    top: 7px;
    opacity: ${props => (props.isOpen ? 0 : 1)};
  }
  span:nth-of-type(3) {
    top: 14px;
    transform: ${props => (props.isOpen ? 'translateY(-7px) rotate(-45deg)' : 'none')};
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease, visibility 0.25s ease;

  @media (min-width: ${baseTheme.breakpoints.lg}) {
    display: none;
  }
`;

const MenuPanel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  width: min(${MENU_PANEL_WIDTH}px, 90vw);
  max-width: 100%;
  height: 100vh;
  background: var(--al-bg);
  border-left: 1px solid var(--al-border);
  z-index: 1002;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.3);
  transform: translateX(${props => (props.isOpen ? 0 : '100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  overflow-y: auto;
  overflow-x: hidden;
`;

const MenuHeader = styled.div`
  box-sizing: border-box;
  height: ${NAV_HEIGHT}px;
  padding: 0 ${baseTheme.spacing.lg};
  border-bottom: 1px solid var(--al-border);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${baseTheme.spacing.md};
`;

const MenuTitle = styled.div`
  ${navTypography}
  font-size: 0.7rem;
  color: var(--al-text-dim);
  letter-spacing: 0.15em;
`;

const MenuCloseButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 0;
  margin: 0 -8px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--al-text);
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.08);
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const MenuBody = styled.div`
  padding: ${baseTheme.spacing.md} 0 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const MenuSections = styled.div`
  flex: 1;
`;

const Section = styled.div`
  margin-bottom: ${baseTheme.spacing.xs};
`;

const SectionHeader = styled.button`
  width: 100%;
  padding: ${baseTheme.spacing.sm} ${baseTheme.spacing.lg};
  ${navTypography}
  font-size: 0.7rem;
  color: var(--al-text-dim);
  letter-spacing: 0.08em;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.05);
  }

  &:focus {
    outline: none;
  }
`;

const SectionArrow = styled.span`
  font-size: 0.6rem;
  transition: transform 0.2s ease;
  transform: rotate(${props => (props.isExpanded ? 180 : 0)}deg);
`;

const SectionContent = styled.div`
  overflow: hidden;
  max-height: ${props => (props.isExpanded ? '500px' : '0')};
  transition: max-height 0.25s ease;
`;

const MenuLink = styled(Link)`
  display: block;
  padding: ${baseTheme.spacing.sm} ${baseTheme.spacing.lg} ${baseTheme.spacing.sm} ${baseTheme.spacing.xl};
  font-size: 0.8rem;
  font-weight: ${baseTheme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => (p.$active ? 'var(--al-primary)' : 'var(--al-text)')};
  text-decoration: none;
  border-left: 3px solid ${(p) => (p.$active ? 'var(--al-primary)' : 'transparent')};
  background-color: ${(p) => (p.$active ? 'rgba(var(--al-primary-rgb), 0.08)' : 'transparent')};
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.06);
  }
`;

const ROUTE_SECTIONS = {
  preproduction: ['/screenlist2', '/film-list', '/ink-manager'],
  orders: ['/order-inbox', '/quotes', '/invoicing', '/archive'],
  inbound: ['/ordering', '/api-ordering', '/receiving', '/receiving/api'],
  records: ['/customers', '/products', '/ink-inventory', '/thread-manager'],
};

function getSectionForPath(path) {
  for (const [section, routes] of Object.entries(ROUTE_SECTIONS)) {
    if (routes.some(route => path === route || path.startsWith(route + '/'))) return section;
  }
  return null;
}

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    preproduction: false,
    orders: false,
    inbound: false,
    records: false,
  });
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const { facilityId, setFacilityId, isStrawberryPatch, facilities } = useFacility();

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (menuOpen && location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
    }
  }, [menuOpen, location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      const section = getSectionForPath(location.pathname);
      if (section) {
        setExpandedSections(prev => ({ ...prev, [section]: true }));
      }
    }
  }, [menuOpen, location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const toggleSection = (e, section) => {
    e.stopPropagation();
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const mainNavItems = [
    { to: '/printlist2', label: 'Printlist' },
    { to: '/orders', label: 'Orders' },
    { to: '/lines', label: 'Lines' },
  ];

  const menuSections = [
    {
      key: 'preproduction',
      label: 'Preproduction',
      links: [
        { to: '/screenlist2', label: 'Screen List' },
        { to: '/ink-manager', label: 'Ink Planner' },
        { to: '/film-list', label: 'Film List' },
      ],
    },
    {
      key: 'orders',
      label: 'Orders',
      links: [
        { to: '/order-inbox', label: 'Inbox' },
        { to: '/quotes', label: 'Quotes' },
        { to: '/invoicing', label: 'Invoicing' },
        { to: '/archive', label: 'Archive' },
      ],
    },
    {
      key: 'inbound',
      label: 'Inbound',
      links: [
        { to: '/ordering', label: 'Ordering' },
        { to: '/api-ordering', label: 'API Ordering (Beta)' },
        { to: '/receiving', label: 'Receiving' },
        { to: '/receiving/api', label: 'API Receiving (Beta)' },
      ],
    },
    {
      key: 'records',
      label: 'Records',
      links: [
        { to: '/customers', label: 'Customers' },
        { to: '/products', label: 'Products' },
        { to: '/ink-inventory', label: 'Ink Stock' },
        { to: '/thread-manager', label: 'Thread Stock' },
      ],
    },
  ];

  return (
    <>
      <NavContainer $sp={isStrawberryPatch}>
        <LogoLink to="/">AL</LogoLink>

        <CompactNav aria-label="Primary pages">
          {mainNavItems.map(({ to, label }) => (
            <CompactNavItem key={to} $active={location.pathname === to}>
              <Link to={to}>{label}</Link>
            </CompactNavItem>
          ))}
        </CompactNav>

        <NavRight $menuOpen={menuOpen}>
          <MainNav>
            {mainNavItems.map(({ to, label }) => (
              <NavItem key={to} isActive={location.pathname === to}>
                <Link to={to}>{label}</Link>
              </NavItem>
            ))}
          </MainNav>

          {!menuOpen && (
            <HamburgerWrap>
              <HamburgerButton
                type="button"
                onClick={openMenu}
                aria-expanded={false}
                aria-label="Open menu"
              >
                <HamburgerIcon isOpen={false}>
                  <span />
                  <span />
                  <span />
                </HamburgerIcon>
              </HamburgerButton>
            </HamburgerWrap>
          )}
        </NavRight>
      </NavContainer>

      <Backdrop isVisible={menuOpen} onClick={closeMenu} aria-hidden="true" />

      <MenuPanel isOpen={menuOpen} role="dialog" aria-modal="true" aria-label="Navigation menu">
        <MenuHeader>
          <MenuTitle>Menu</MenuTitle>
          <MenuCloseButton type="button" onClick={closeMenu} aria-label="Close menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </MenuCloseButton>
        </MenuHeader>
        <MenuBody>
          <MenuSections>
            {menuSections.map(({ key, label, links }) => (
              <Section key={key}>
                <SectionHeader onClick={(e) => toggleSection(e, key)}>
                  {label}
                  <SectionArrow isExpanded={expandedSections[key]}>▼</SectionArrow>
                </SectionHeader>
                <SectionContent isExpanded={expandedSections[key]}>
                  {links.map(({ to, label: linkLabel }) => (
                    <MenuLink
                      key={to}
                      to={to}
                      $active={location.pathname === to || location.pathname.startsWith(to + '/')}
                      onClick={closeMenu}
                    >
                      {linkLabel}
                    </MenuLink>
                  ))}
                </SectionContent>
              </Section>
            ))}
          </MenuSections>

          <FacilityMenuSection>
            <FacilityMenuLabel>Production facility</FacilityMenuLabel>
            <FacilitySwitcher $sp={isStrawberryPatch} role="group" aria-label="Production facility">
              {facilities.map((f) => {
                const active = facilityId === f.id;
                const spActive = active && f.id === FACILITY_IDS.STRAWBERRY_PATCH;
                return (
                  <FacilityOption
                    key={f.id}
                    type="button"
                    $active={active}
                    $spActive={spActive}
                    $spChrome={isStrawberryPatch}
                    aria-pressed={active}
                    onClick={() => setFacilityId(f.id)}
                    title={f.label}
                  >
                    {f.id === FACILITY_IDS.STRAWBERRY_PATCH ? (
                      <>
                        <FacilityCapsWord>S.</FacilityCapsWord>
                        <FacilityScriptWord $onVine={spActive}>Patch</FacilityScriptWord>
                      </>
                    ) : (
                      f.shortLabel
                    )}
                  </FacilityOption>
                );
              })}
            </FacilitySwitcher>
          </FacilityMenuSection>
        </MenuBody>
      </MenuPanel>
    </>
  );
}

export default Navbar;
